import { useState, useEffect } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import StatisticsCards from './components/StatisticsCards';
import SearchFilters, { type SearchFilters as Filters } from './components/SearchFilters';
import CloudPCTable from './components/CloudPCTable';
import CloudPCDetailModal from './components/CloudPCDetailModal';
import ConfirmModal from './components/ConfirmModal';
import { mockCloudPCs, mockCloudPCDetail, mockSnapshots, mockOperationRecords } from './mockData';
import type { CloudPC } from './types';

export default function CloudPCsPage() {
  const [data, setData] = useState<CloudPC[]>(mockCloudPCs);
  const [filteredData, setFilteredData] = useState<CloudPC[]>(mockCloudPCs);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [detailModalData, setDetailModalData] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'info';
    requireInput?: boolean;
    inputPlaceholder?: string;
    expectedInput?: string;
    onConfirm: () => void;
  } | null>(null);

  // 自动刷新
  useEffect(() => {
    const interval = setInterval(() => {
      // 模拟数据刷新
      console.log('数据自动刷新...');
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleSearch = (filters: Filters) => {
    let result = [...data];

    if (filters.searchValue) {
      result = result.filter((item) => {
        if (filters.searchType === 'id') {
          return item.id.toLowerCase().includes(filters.searchValue.toLowerCase());
        } else if (filters.searchType === 'userId') {
          return item.userId.toLowerCase().includes(filters.searchValue.toLowerCase());
        } else {
          return item.userPhone.includes(filters.searchValue);
        }
      });
    }

    if (filters.status !== 'all') {
      result = result.filter((item) => item.status === filters.status);
    }

    if (filters.os.length > 0) {
      result = result.filter((item) => filters.os.includes(item.os));
    }

    if (filters.datacenter) {
      result = result.filter((item) => item.datacenter === filters.datacenter);
    }

    if (filters.isOverdue !== 'all') {
      result = result.filter((item) =>
        filters.isOverdue === 'yes' ? item.isOverdue : !item.isOverdue
      );
    }

    setFilteredData(result);
  };

  const handleBatchStart = () => {
    setConfirmModal({
      show: true,
      title: '批量启动',
      message: `确定要启动选中的 ${selectedIds.length} 个云电脑实例吗？`,
      type: 'info',
      onConfirm: () => {
        alert(`已启动 ${selectedIds.length} 个实例`);
        setSelectedIds([]);
        setConfirmModal(null);
      },
    });
  };

  const handleBatchStop = () => {
    setConfirmModal({
      show: true,
      title: '批量停止',
      message: `确定要停止选中的 ${selectedIds.length} 个云电脑实例吗？`,
      type: 'warning',
      onConfirm: () => {
        alert(`已停止 ${selectedIds.length} 个实例`);
        setSelectedIds([]);
        setConfirmModal(null);
      },
    });
  };

  const handleBatchRestart = () => {
    setConfirmModal({
      show: true,
      title: '批量重启',
      message: `确定要重启选中的 ${selectedIds.length} 个云电脑实例吗？`,
      type: 'warning',
      onConfirm: () => {
        alert(`已重启 ${selectedIds.length} 个实例`);
        setSelectedIds([]);
        setConfirmModal(null);
      },
    });
  };

  const handleBatchDelete = () => {
    setConfirmModal({
      show: true,
      title: '批量删除',
      message: `确定要删除选中的 ${selectedIds.length} 个云电脑实例吗？\n此操作不可恢复！`,
      type: 'danger',
      onConfirm: () => {
        setData(data.filter((item) => !selectedIds.includes(item.id)));
        setFilteredData(filteredData.filter((item) => !selectedIds.includes(item.id)));
        setSelectedIds([]);
        setConfirmModal(null);
        alert('删除成功');
      },
    });
  };

  const handleBatchExport = () => {
    alert(`导出 ${selectedIds.length} 条数据`);
  };

  const handleStart = (id: string) => {
    setConfirmModal({
      show: true,
      title: '启动云电脑',
      message: '确定要启动该云电脑实例吗？',
      type: 'info',
      onConfirm: () => {
        alert(`已启动实例 ${id}`);
        setConfirmModal(null);
      },
    });
  };

  const handleStop = (id: string) => {
    setConfirmModal({
      show: true,
      title: '停止云电脑',
      message: '确定要停止该云电脑实例吗？',
      type: 'warning',
      onConfirm: () => {
        alert(`已停止实例 ${id}`);
        setConfirmModal(null);
      },
    });
  };

  const handleRestart = (id: string) => {
    setConfirmModal({
      show: true,
      title: '重启云电脑',
      message: '确定要重启该云电脑实例吗？',
      type: 'warning',
      onConfirm: () => {
        alert(`已重启实例 ${id}`);
        setConfirmModal(null);
      },
    });
  };

  const handleDelete = (id: string) => {
    setConfirmModal({
      show: true,
      title: '删除云电脑',
      message: '确定要删除该云电脑实例吗？\n此操作不可恢复！\n\n请输入实例ID以确认删除：',
      type: 'danger',
      requireInput: true,
      inputPlaceholder: '请输入实例ID',
      expectedInput: id,
      onConfirm: () => {
        setData(data.filter((item) => item.id !== id));
        setFilteredData(filteredData.filter((item) => item.id !== id));
        setConfirmModal(null);
        alert('删除成功');
      },
    });
  };

  const statistics = {
    totalCount: data.length,
    runningCount: data.filter((item) => item.status === 'running').length,
    todayNewCount: data.filter((item) => item.createTime.startsWith('2024-03-10')).length,
    expiringSoonCount: data.filter(
      (item) => item.remainingTime.includes('小时') && !item.remainingTime.includes('天')
    ).length,
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">云电脑管理</h1>
          <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors whitespace-nowrap">
            <i className="ri-refresh-line mr-2"></i>
            刷新数据
          </button>
        </div>

        <StatisticsCards {...statistics} />

        <SearchFilters onSearch={handleSearch} />

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBatchStart}
              disabled={selectedIds.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <i className="ri-play-line mr-2"></i>
              批量启动
            </button>
            <button
              onClick={handleBatchStop}
              disabled={selectedIds.length === 0}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <i className="ri-stop-line mr-2"></i>
              批量停止
            </button>
            <button
              onClick={handleBatchRestart}
              disabled={selectedIds.length === 0}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <i className="ri-restart-line mr-2"></i>
              批量重启
            </button>
            <button
              onClick={handleBatchDelete}
              disabled={selectedIds.length === 0}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-2"></i>
              批量删除
            </button>
            <button
              onClick={handleBatchExport}
              disabled={selectedIds.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <i className="ri-download-line mr-2"></i>
              批量导出
            </button>
          </div>
          {selectedIds.length > 0 && (
            <span className="text-sm text-gray-600">已选择 {selectedIds.length} 项</span>
          )}
        </div>

        <CloudPCTable
          data={filteredData}
          selectedIds={selectedIds}
          onSelectChange={setSelectedIds}
          onViewDetail={setDetailModalData}
          onStart={handleStart}
          onStop={handleStop}
          onRestart={handleRestart}
          onDelete={handleDelete}
        />

        {detailModalData && (
          <CloudPCDetailModal
            data={mockCloudPCDetail}
            snapshots={mockSnapshots}
            operationRecords={mockOperationRecords}
            onClose={() => setDetailModalData(null)}
          />
        )}

        {confirmModal && (
          <ConfirmModal
            title={confirmModal.title}
            message={confirmModal.message}
            type={confirmModal.type}
            requireInput={confirmModal.requireInput}
            inputPlaceholder={confirmModal.inputPlaceholder}
            expectedInput={confirmModal.expectedInput}
            onConfirm={confirmModal.onConfirm}
            onCancel={() => setConfirmModal(null)}
          />
        )}
      </div>
    </MainLayout>
  );
}
